import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { getFlyerDesigns, type DesignAsset } from "@/services/localAssets";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";

export default function FlyerGallery() {
  const [designs, setDesigns] = useState<DesignAsset[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    try {
      setIsLoading(true);
      const flyerDesigns = getFlyerDesigns();
      setDesigns(flyerDesigns);
    } catch (err) {
      console.error("Error fetching flyer designs:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Image error handler
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.warn("Image failed to load:", e.currentTarget.src);
    e.currentTarget.src = "https://via.placeholder.com/300x300?text=Image+Not+Found";
  };

  return (
    <div className="py-20 px-4 bg-accent-navy min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-10">
          <h1 className="text-4xl font-bold text-white">Flyer Designs</h1>
          <Link 
            to="/" 
            className="coral text-coral px-4 py-2 rounded hover:coral transition-colors"
          >
            Back to Home
          </Link>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Array(8)
              .fill(0)
              .map((_, index) => (
                <div key={index} className="aspect-square rounded-lg overflow-hidden">
                  <Skeleton className="h-full w-full" />
                </div>
              ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {designs.map((design) => (
              <motion.div
                key={design.id}
                whileHover={{ scale: 1.03 }}
                className="relative aspect-square rounded-lg overflow-hidden shadow-lg"
              >
                <img
                  src={design.url}
                  alt={design.title}
                  className="object-cover w-full h-full"
                  onError={handleImageError}
                />
                <div className="absolute inset-0 bg-black/70 flex flex-col justify-end p-4 opacity-0 hover:opacity-100 transition-opacity">
                  <h3 className="text-white font-semibold truncate">{design.title}</h3>
                  <p className="text-gray-200 text-sm truncate">{design.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
} 